import { nonBreakableHyphen } from '@ipfabric/frontend-shared-assets/utils/characters';
import {
  type PathLookupCheck,
  isGraphPathLookupHostToDefaultGWParameters,
} from '@ipfabric/utils';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import { memo, useState } from 'react';
import type { FC, MouseEvent } from 'react';
import { getPassingTrafficResult } from '../helpers/getPassingTrafficResult';
import { MulticastIcon } from '../icons/MulticastIcon';
import { UnicastIcon } from '../icons/UnicastIcon';

interface Props {
  item: PathLookupCheck;
  onDelete: (checkId: string) => void;
  onFetchGraph: (item: PathLookupCheck) => void;
}

const BareGraphPathChecksItem: FC<Props> = (props) => {
  const msg = useLocale();
  const [expanded, setExpanded] = useState<boolean>(false);
  const detectedState = getPassingTrafficResult(
    props.item.passingTraffic,
    props.item.expectedPassingTraffic,
  );

  const firstHopAlgorithm = (() => {
    if (isGraphPathLookupHostToDefaultGWParameters(props.item.parameters)) {
      return '(Empty)';
    }
    return props.item.parameters.firstHopAlgorithm.type === 'userDefined'
      ? msg.canvasGraph.graphSidePanel.pathChecks.item.custom
      : props.item.parameters.firstHopAlgorithm.type;
  })();

  const formatPorts = (ports: string | null) =>
    ports?.replaceAll('-', nonBreakableHyphen).replaceAll(',', ', ');

  const handleToggleButtonClick = (e: MouseEvent) => {
    e.stopPropagation();

    setExpanded(!expanded);
  };

  const handleDeleteButtonClick = (e: MouseEvent) => {
    e.stopPropagation();

    props.onDelete(props.item.id);
  };

  const getBooleanText = (bool: boolean) =>
    bool
      ? msg.canvasGraph.graphSidePanel.pathChecks.item.yes
      : msg.canvasGraph.graphSidePanel.pathChecks.item.no;

  return (
    <>
      <div className='pathlookup-checks__container'>
        <div
          className='flex flex-1 cursor-pointer pt-1.5'
          onClick={() => {
            props.onFetchGraph(props.item);
          }}
        >
          <div className='pathlookup-checks__badge'>
            {props.item.type === 'unicast' ? (
              <UnicastIcon title='Unicast' width='18' height='17' />
            ) : (
              <MulticastIcon title='Multicast' width='18' height='18' />
            )}
            <div
              className={classNames(
                'badge mt-1 w-10',
                props.item.passingTraffic === props.item.expectedPassingTraffic
                  ? 'pathlookup-checks__badge--pass'
                  : 'pathlookup-checks__badge--fail',
              )}
            >
              {props.item.passingTraffic === 'error'
                ? msg.canvasGraph.graphSidePanel.pathChecks.item
                    .detectedStateValue.errorShort
                : msg.canvasGraph.graphSidePanel.pathChecks.item
                    .detectedStateValue[detectedState]}
            </div>
          </div>
          <div className='flex flex-1 flex-col'>
            <div className='flex flex-1'>
              <div className='mr-2 flex-1'>
                <div className='pathlookup-checks__label pathlookup-checks__label--main'>
                  {msg.canvasGraph.graphSidePanel.pathChecks.item.source}
                </div>
                <div className='pathlookup-checks__data'>{props.item.src}</div>
              </div>
              <div className='mr-2 flex-1'>
                <div className='pathlookup-checks__label pathlookup-checks__label--main'>
                  {msg.canvasGraph.graphSidePanel.pathChecks.item.destination}
                </div>
                <div className='pathlookup-checks__data'>{props.item.dst}</div>
              </div>
            </div>
            <div className='mt-4 flex flex-1'>
              <div className='mr-2 flex-1'>
                <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                  {msg.canvasGraph.graphSidePanel.pathChecks.item.ports}
                </div>
                <div className='pathlookup-checks__data'>
                  {formatPorts(props.item.srcPorts) ||
                    msg.canvasGraph.graphSidePanel.pathChecks.item.empty}
                </div>
              </div>
              <div className='mr-2 flex-1'>
                <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                  {msg.canvasGraph.graphSidePanel.pathChecks.item.ports}
                </div>
                <div className='pathlookup-checks__data'>
                  {formatPorts(props.item.dstPorts) ||
                    msg.canvasGraph.graphSidePanel.pathChecks.item.empty}
                </div>
              </div>
            </div>
            {expanded && (
              <>
                <hr className='graph-pathlookup-divider mb-4 mt-2 w-full' />
                <div className='flex flex-1'>
                  <div className='mr-2 flex-1'>
                    <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                      {
                        msg.canvasGraph.graphSidePanel.pathChecks.item
                          .firstHopAlgorithm
                      }
                    </div>
                    <div className='pathlookup-checks__data'>
                      {firstHopAlgorithm}
                    </div>
                  </div>
                  <div className='mr-2 flex-1'>
                    <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                      {msg.canvasGraph.graphSidePanel.pathChecks.item.vrf}
                    </div>
                    <div className='pathlookup-checks__data'>
                      {props.item.vrf === '' &&
                        msg.canvasGraph.graphSidePanel.pathChecks.item.empty}
                      {props.item.vrf === null &&
                        msg.canvasGraph.graphSidePanel.pathChecks.item
                          .autoDetect}
                      {props.item.vrf}
                    </div>
                  </div>
                </div>
                {props.item.type === 'multicast' && (
                  <div className='mt-4 flex flex-1'>
                    <div className='mr-2 flex-1'>
                      <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                        {
                          msg.canvasGraph.graphSidePanel.pathChecks.item
                            .receiver
                        }
                      </div>
                      <div className='pathlookup-checks__data'>
                        {props.item.receiver}
                      </div>
                    </div>
                    <div className='mr-2 flex-1'>
                      <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                        {
                          msg.canvasGraph.graphSidePanel.pathChecks.item
                            .securedPath
                        }
                      </div>
                      <div className='pathlookup-checks__data'>
                        {getBooleanText(props.item.securedPath)}
                      </div>
                    </div>
                  </div>
                )}
                <div className='mt-4 flex flex-1'>
                  <div className='mr-2 flex-1'>
                    <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                      {
                        msg.canvasGraph.graphSidePanel.pathChecks.item
                          .indetndedStatus
                      }
                    </div>
                    <div className='pathlookup-checks__data capitalize'>
                      {
                        msg.canvasGraph.graphSidePanel.pathChecks.item
                          .intendedStateValue[props.item.expectedPassingTraffic]
                      }
                    </div>
                  </div>
                  <div className='mr-2 flex-1'>
                    <div className='pathlookup-checks__label pathlookup-checks__label--minor'>
                      {
                        msg.canvasGraph.graphSidePanel.pathChecks.item
                          .detectedState
                      }
                    </div>
                    <div
                      className={classNames(
                        'pathlookup-checks__data capitalize',
                        props.item.passingTraffic === 'error' &&
                          'pathlookup-checks__data--error',
                      )}
                    >
                      {
                        msg.canvasGraph.graphSidePanel.pathChecks.item
                          .detectedStateValue[detectedState]
                      }
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className='flex flex-col justify-between'>
          <Button
            className='ml-2'
            variant='tertiary'
            onClick={handleToggleButtonClick}
            type='button'
            icon={expanded ? 'fa-chevron-up' : 'fa-chevron-down'}
            iconStyle='solid'
            iconPosition='right'
          >
            {msg.canvasGraph.graphSidePanel.pathChecks.detail}
          </Button>
          {expanded && (
            <Button
              className='ml-6'
              variant='secondary'
              accent='negative'
              onClick={handleDeleteButtonClick}
              icon='fa-trash-can'
              type='button'
            />
          )}
        </div>
      </div>
      <hr className='graph-pathlookup-divider mx-5 my-0' />
    </>
  );
};

export const GraphPathChecksItem = memo(BareGraphPathChecksItem);
